import { h } from '@omni-torn/ui';
import {
  findDayRows,
  findItemRows,
  findKindInfo,
  findLogCategory,
  findProfit,
  findsCsvRows,
  findsPerDay,
  findTotals,
  findWorth,
  inRange,
  rangeText,
  readFindLog,
  tigerDaySummary,
  type CalendarEntry,
  type DateRange,
  type FindKind,
  type FindLine,
  type FindMeasure,
} from '@omni-torn/shared';
import { forgetLogText, readingLogText } from '../own-history';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { firstRead, LogReader, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangePicker, zoomChart } from '../log-kit/ui';
import { torn } from '../session';
import { box, loading, money, num, problem, signed, table, tct } from '../ui';

/**
 * Events → Hunting, Dump and City finds (website roadmap S38 — torn.report's Hunting and Dump Search pages and the City
 * Finder log): from your own log (Full key), read into this browser by the log kit (S09) — Torn's "Hunting", "Dump" and
 * city-finds categories found by name in `torn/logcategories` (else the whole log, the lines picked out here). Per day
 * (worth, profit, its running total; World Tiger Day marked), per item, at Torn's market value today (the Omni-Torn
 * server's copy of Torn's items, none of your calls). Torn calls: the categories (kept a day) and the newest pages of the
 * tab's stream, then only on "Read newer/older". Nothing goes to or is kept on the Omni-Torn server.
 */

const DAY = 86_400_000;
const MEASURES: Record<FindKind, ReadonlyArray<readonly [FindMeasure, string]>> = {
  hunting: [
    ['profit', 'Profit'],
    ['cumulative', 'Profit, running total'],
    ['worth', 'Brought in'],
    ['lines', 'Hunts'],
  ],
  dump: [
    ['worth', 'Worth found'],
    ['items', 'Items found'],
    ['lines', 'Searches'],
  ],
  city: [
    ['worth', 'Worth found'],
    ['items', 'Items found'],
  ],
};

/** One reader per player and tab (its own category stream), kept while the site is open. */
const readers = new Map<string, { store: LogStore; reader: LogReader; first: Promise<string> | null }>();

const tile = (label: string, value: string, data = '', cls = '', title = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}), ...(title ? { title } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: `tile-value ${cls}`.trim() }, value));
const tone = (n: number) => (n > 0 ? 't-green' : n < 0 ? 't-red' : '');
const moneySigned = (n: number) => signed(Math.round(n), (x) => money(x));
const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

export interface FindsContext {
  pid: number;
  kind: FindKind;
  range: () => DateRange;
  onRange: (r: DateRange) => void;
  items: () => ReadonlyMap<number, RefItem>;
  calendar: () => readonly CalendarEntry[] | null;
}

async function readerFor(pid: number, kind: FindKind): Promise<{ store: LogStore; reader: LogReader; first: Promise<string> | null }> {
  const key = `${pid}:${kind}`;
  const have = readers.get(key);
  if (have) return have;
  const store = await logStore(pid);
  let cat: number | null = null;
  try {
    cat = findLogCategory(await torn('/v2/torn/logcategories', {}, DAY), findKindInfo(kind).category);
  } catch {
    /* the whole log */
  }
  const stream: LogStream = cat ? { id: `cat:${cat}`, cat } : { id: 'all' };
  const r = { store, reader: new LogReader(store, stream), first: null };
  readers.set(key, r);
  return r;
}

/** The tab's report (the caller has checked the key can read the log). */
export function findsPanel(ctx: FindsContext): HTMLElement {
  const out = h('div', { 'data-finds': ctx.kind }, loading(readingLogText()));
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });
  let measure: FindMeasure = MEASURES[ctx.kind][0]![0];

  async function fill(): Promise<void> {
    const r = await readerFor(ctx.pid, ctx.kind);
    const range = ctx.range();
    const reread = () => void fill();
    const info = findKindInfo(ctx.kind);
    const items = ctx.items();
    const values = new Map([...items.values()].map((i) => [i.id, i.marketValue] as const));
    const name = (id: number) => items.get(id)?.name ?? `Item ${id}`;
    const all = readFindLog(await r.store.range({ from: null, to: null }), ctx.kind);
    const shown = all.filter((l) => inRange(l.at, range));
    const kept = await keptLogPanel({
      reader: r.reader,
      store: r.store,
      range,
      matching: all.length,
      what: { hunting: 'hunts', dump: 'dump searches', city: 'city finds' }[ctx.kind],
      note: logNote,
      forgetText: forgetLogText(),
      onForget: () => {
        for (const k of [...readers.keys()]) if (k.startsWith(`${ctx.pid}:`)) readers.delete(k);
      },
      redraw: reread,
    });
    const calendar = ctx.calendar() ?? [];
    const t = findTotals(all, range, values);
    const hunting = ctx.kind === 'hunting';
    const days = findDayRows(all, range, values, calendar);
    const tiger = tigerDaySummary(days);
    const tiles = h(
      'div',
      { class: 'tiles' },
      tile(hunting ? 'Hunts' : ctx.kind === 'dump' ? 'Searches' : 'Finds', num(t.lines), 'fd-lines'),
      tile('Days', num(t.days), 'fd-days'),
      tile('Items found', num(t.items), 'fd-items'),
      tile('Worth today', money(t.worth), 'fd-worth', '', 'Money plus the items at Torn’s market value today'),
      ...(hunting ? [tile('Cost', money(t.cost), 'fd-cost', '', 'What your log says the hunts cost (fees, items used)'), tile('Profit', moneySigned(t.profit), 'fd-profit', tone(t.profit)), tile('Skill gained', t.skill ? `+${num(t.skill, 2)}` : '—', 'fd-skill')] : []),
      ...(hunting && tiger.tigerDays ? [tile('World Tiger Day', `${moneySigned(tiger.tigerProfit)} (${num(tiger.tigerDays)} day${tiger.tigerDays === 1 ? '' : 's'})`, 'fd-tiger', tone(tiger.tigerProfit), `Average day: ${tiger.otherAvg === null ? '—' : money(tiger.otherAvg)}`)] : []),
    );
    const ms = MEASURES[ctx.kind];
    if (!ms.some(([m]) => m === measure)) measure = ms[0]![0];
    const label = ms.find(([m]) => m === measure)![1];
    const sel = filterSelect('Chart', ms, measure, (v) => {
      measure = v as FindMeasure;
      reread();
    }, 'measure');
    const isMoney = measure !== 'items' && measure !== 'lines';
    const chart = box(h('h2', {}, `${label} per day`), zoomChart(findsPerDay(all, measure, range, Date.now(), values), { label, format: isMoney ? (x: number) => `${x < 0 ? '−' : ''}$${Math.abs(Math.round(x)).toLocaleString('en-US')}` : (x: number) => (Number.isInteger(x) ? num(x) : ''), onZoom: ctx.onRange }), { id: 'fd-chart', extra: sel });

    const dayBox = box('By day', [
      table([...days].reverse(), [
        { label: 'Day', get: (d) => h('span', {}, day(d.day), d.tiger ? h('strong', { class: 'small', 'data-tiger': '', title: d.tiger.from === 'calendar' ? 'In Torn’s calendar' : '29 July' }, ' · World Tiger Day') : null), sort: (d) => d.day },
        { label: hunting ? 'Hunts' : 'Lines', get: (d) => num(d.lines), sort: (d) => d.lines, cls: 'r' },
        { label: 'Items', get: (d) => num(d.items), sort: (d) => d.items, cls: 'r' },
        { label: 'Worth', get: (d) => money(d.worth), sort: (d) => d.worth, cls: 'r' },
        ...(hunting
          ? [
              { label: 'Cost', get: (d: (typeof days)[number]) => money(d.cost), sort: (d: (typeof days)[number]) => d.cost, cls: 'r' },
              { label: 'Profit', get: (d: (typeof days)[number]) => h('span', { class: tone(d.profit) }, moneySigned(d.profit)), sort: (d: (typeof days)[number]) => d.profit, cls: 'r' },
              { label: 'Running total', get: (d: (typeof days)[number]) => moneySigned(d.cumulative), sort: (d: (typeof days)[number]) => d.day, cls: 'r' },
            ]
          : []),
      ], { initial: 0, empty: 'Nothing in this range.' }),
      hunting ? h('p', { class: 'muted small' }, 'World Tiger Day: 29 July (or the day Torn’s calendar names for it).') : null,
    ].filter(Boolean) as HTMLElement[], { id: 'fd-days' });

    const itemRows = findItemRows(all, range, values);
    const itemBox = box(hunting ? 'What your hunts brought in' : 'Items found', [
      table(itemRows.filter((i) => i.found > 0), [
        { label: 'Item', get: (i) => h('a', { href: `/market/${i.id}`, 'data-link': true }, name(i.id)), sort: (i) => name(i.id) },
        { label: 'Found', get: (i) => num(i.found), sort: (i) => i.found, cls: 'r' },
        { label: 'Each today', get: (i) => money(i.each), sort: (i) => i.each ?? -1, cls: 'r' },
        { label: 'Worth today', get: (i) => money(i.worth), sort: (i) => i.worth ?? -1, cls: 'r' },
        { label: 'Last', get: (i) => tct(i.last), sort: (i) => i.last },
      ], { initial: 3, empty: 'No items in this range.' }),
      t.unvalued ? h('p', { class: 'muted small', 'data-uncounted': '' }, `${num(t.unvalued)} found without a market value today — left out of the worth, never guessed.`) : null,
    ].filter(Boolean) as HTMLElement[], { id: 'fd-items' });

    const csv = csvButton(() => `${ctx.kind}-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => findsCsvRows(shown, values, name));
    const lineBox = box('Log lines', [
      table([...shown].reverse().slice(0, 500), [
        { label: 'When', get: (l: FindLine) => h('span', { title: new Date(l.at).toUTCString() }, tct(l.at)), sort: (l: FindLine) => l.at },
        { label: 'What', get: (l: FindLine) => l.title, sort: (l: FindLine) => l.title },
        { label: 'Found', get: (l: FindLine) => l.gained.map((i) => `${name(i.id)}${i.amount > 1 ? ` ×${num(i.amount)}` : ''}`).join(', ') || (l.money ? money(l.money) : '—') },
        { label: 'Worth', get: (l: FindLine) => money(findWorth(l, values)), sort: (l: FindLine) => findWorth(l, values), cls: 'r' },
        ...(hunting ? [{ label: 'Profit', get: (l: FindLine) => moneySigned(findProfit(l, values)), sort: (l: FindLine) => findProfit(l, values), cls: 'r' }] : []),
      ], { csv: false, initial: 0, empty: 'Nothing in this range.' }),
      shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
      h('p', { class: 'muted small' }, `Torn doesn’t describe these log lines, so they’re read by Torn’s category and title and the items they name. Worth is at Torn’s market value today, not what you sold for. ${info.blurb}`),
    ].filter(Boolean) as HTMLElement[], { id: 'fd-lines', extra: csv });

    out.replaceChildren(kept, rangePicker(range, ctx.onRange), tiles, chart, h('div', { class: 'cards two' }, dayBox, itemBox), lineBox);
  }

  void (async () => {
    try {
      const r = await readerFor(ctx.pid, ctx.kind);
      r.first ??= firstRead(r.reader);
      try {
        logNote.textContent = await r.first;
      } catch (err) {
        // A failed Torn read is the note's problem, not the page's (the kept lines still show).
        r.first = null;
        logNote.replaceChildren(problem(err));
      }
      if (out.isConnected) await fill();
    } catch (err) {
      out.replaceChildren(problem(err));
    }
  })();
  return out;
}
